/**
 * Shared helpers for talking to Server-Sent Event endpoints.
 *
 * Used by the issues chat assistant and the project site-notes assistant.
 * Both produce the same SSE envelope from the backend:
 *
 *   data: {"type":"text_delta","text":"..."}\n\n
 *   data: {"type":"done", ...}\n\n
 *   data: {"type":"error","message":"..."}\n\n
 *
 * Plus chat-specific event types (``ui_component``, ``tool_call_start``,
 * etc.) that the issues runtime knows about. The reader doesn't care —
 * each parsed frame is returned as a generic ``ParsedEvent`` and the
 * caller branches on ``event.type``.
 */

import { getAccessToken } from "@/lib/auth/token-store"

const API_BASE_URL = String(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import.meta as any).env?.VITE_API_BASE_URL ?? "",
)

export interface ParsedEvent {
  type: string
  text?: string
  message?: string
  thread_id?: number
  issue_id?: number
  component?: string
  [key: string]: unknown
}

export function getAuthToken(): string {
  return getAccessToken() || ""
}

function getAuthorizationHeader(): string {
  const access = getAccessToken()
  return access ? `Bearer ${access}` : ""
}

/**
 * POST a JSON body to an SSE endpoint and return the raw fetch Response
 * (caller is expected to consume it via {@link readSseEvents}).
 */
export async function postSseRequest(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`
  const authorization = getAuthorizationHeader()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (authorization) headers.Authorization = authorization

  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    throw new Error(`SSE request failed: ${response.status}`)
  }
  return response
}

/**
 * Iterate parsed SSE frames from a fetch ``Response`` body.
 *
 * Frames are separated by a blank line ("\n\n"). Network reads can arrive
 * mid-frame, so we maintain a rolling buffer and only parse once a
 * complete frame boundary is in hand. Splitting per chunk on "\n" alone
 * silently drops any partial JSON across chunk boundaries.
 */
export async function* readSseEvents(
  response: Response,
): AsyncGenerator<ParsedEvent, void, unknown> {
  const reader = response.body?.getReader()
  if (!reader) throw new Error("No response body")

  const decoder = new TextDecoder()
  let buffer = ""

  const parseFrame = (raw: string): ParsedEvent | null => {
    // A frame may contain multiple "data: " lines (per the SSE spec); the
    // payload is the concatenation joined with newlines. Our backend
    // emits one "data: <json>" per frame, but be defensive.
    const dataLines: string[] = []
    for (const line of raw.split("\n")) {
      if (line.startsWith("data: ")) dataLines.push(line.slice(6))
      else if (line.startsWith("data:")) dataLines.push(line.slice(5))
    }
    if (dataLines.length === 0) return null
    try {
      return JSON.parse(dataLines.join("\n")) as ParsedEvent
    } catch {
      return null
    }
  }

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        const trailing = buffer.trim()
        if (trailing) {
          const event = parseFrame(trailing)
          if (event) yield event
        }
        return
      }

      buffer += decoder.decode(value, { stream: true })
      let sep: number
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)
        const event = parseFrame(frame)
        if (event) yield event
      }
    }
  } finally {
    reader.releaseLock()
  }
}
